import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import VitrinePromocional from '../components/VitrinePromocional';
import {
  User, FileText, AlertTriangle, Clock, CheckCircle,
  ShoppingCart, Download, ChevronRight, Receipt, DollarSign, Flame, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtDate = (s) => {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
};

export default function ClienteDashboard() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [dash, setDash] = useState(null);
  const [romaneios, setRomaneios] = useState([]);
  const [destaques, setDestaques] = useState(null);
  const [minOrder, setMinOrder] = useState(0);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'cliente') { navigate('/login'); return; }
    carregar();
  }, [authLoading, user]);

  // Auto-rotate banner a cada 5s
  useEffect(() => {
    const banners = destaques?.banners || [];
    if (banners.length < 2) return;
    const t = setInterval(() => setBannerIdx(i => (i + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [destaques?.banners]);

  const carregar = async () => {
    try {
      const [p, d, r, dst, m] = await Promise.all([
        axios.get(`${API}/cliente/me/profile`),
        axios.get(`${API}/cliente/me/dashboard`),
        axios.get(`${API}/cliente/me/romaneios`),
        axios.get(`${API}/cliente/me/destaques`).catch(() => ({ data: null })),
        axios.get(`${API}/cliente/me/min-order`).catch(() => ({ data: { valor_minimo: 0 } })),
      ]);
      setProfile(p.data);
      setDash(d.data);
      setRomaneios(r.data);
      setDestaques(dst.data);
      setMinOrder(Number(m.data?.valor_minimo) || 0);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    }
    setLoading(false);
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-gray-900 dark:to-gray-800">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="portal-page-bg min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
      {/* === FAIXA DE PEDIDO MÍNIMO (sticky no topo) === */}
      {minOrder > 0 && (
        <div
          className="sticky top-0 z-30 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white shadow-lg"
          data-testid="cliente-min-order-banner"
        >
          <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-center flex-wrap">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-bold">
              Pedido mínimo: {fmtBRL(minOrder)}
            </span>
            <span className="text-xs opacity-90 hidden sm:inline">
              — Seu carrinho precisa atingir esse valor para o pedido ser enviado
            </span>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-5">
        {/* Boas-vindas */}
        <div className="flex items-center gap-3" data-testid="cliente-welcome">
          {profile?.profile_image ? (
            <img src={profile.profile_image} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-emerald-500" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <User className="w-6 h-6 text-emerald-600" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
              Olá, {profile?.name?.split(' ')[0] || 'Cliente'} 👋
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{profile?.client?.nome || profile?.email}</p>
          </div>
        </div>

        {/* Cards de estatísticas — MOVIDO PARA O TOPO */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cliente-stats">
          <StatCard icon={FileText} label="Pedidos Totais" value={dash?.total_romaneios || 0} color="blue" />
          <StatCard icon={Clock} label="Em Aberto" value={dash?.em_aberto || 0} subtitle={fmtBRL(dash?.total_em_aberto || 0)} color="amber" />
          <StatCard icon={AlertTriangle} label="Atrasados" value={dash?.atrasados || 0} color="red" />
          <StatCard icon={Receipt} label="Boletos a Vencer" value={dash?.boletos_proximos || 0} subtitle={`${dash?.boletos_atrasados || 0} atrasados`} color="purple" />
        </div>

        {/* Vitrines Promocionais (Mais Vendidos, Ofertas, etc.) */}
        <VitrinePromocional portal="cliente" onProductClick={(p) => navigate(`/cliente/pedido?produto=${p.id}`)} showPrice />

        {/* Lista de romaneios — MOVIDA PARA O TOPO */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="font-bold text-sm text-gray-900 dark:text-white">Meus Pedidos</h2>
            <span className="text-xs text-gray-500">{romaneios.length} total</span>
          </div>
          {romaneios.length === 0 ? (
            <div className="p-8 text-center" data-testid="cliente-no-romaneios">
              <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Nenhum pedido ainda. Clique em &ldquo;Monte seu Pedido&rdquo; abaixo.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {romaneios.slice(0, 5).map(r => (
                <Link
                  key={r.id}
                  to={`/cliente/romaneio/${r.id}`}
                  className="block px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                  data-testid={`cliente-romaneio-${r.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-sm text-gray-900 dark:text-white">#{r.numero_pedido}</span>
                        {r.pendente_validacao_admin && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-bold">⏳ AGUARDANDO ADMIN</span>
                        )}
                        {!r.pendente_validacao_admin && r.origem === 'portal_cliente' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-bold">✓ CONFIRMADO</span>
                        )}
                        {r.is_orcamento && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">ORÇAMENTO</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{fmtDate(r.data || r.created_at)} · {r.items?.length || 0} item(s)</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm text-gray-900 dark:text-white">{fmtBRL(r.valor_total)}</p>
                      <p className="text-[10px] text-gray-500">{r.forma_pagamento || '-'}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </Link>
              ))}
              {romaneios.length > 5 && (
                <button
                  onClick={() => navigate('/cliente/meus-pedidos')}
                  className="w-full py-2 text-xs text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 font-semibold"
                  data-testid="cliente-ver-todos-pedidos"
                >
                  Ver todos os {romaneios.length} pedidos →
                </button>
              )}
            </div>
          )}
        </div>

        {/* CTA: Novo pedido */}
        <button
          onClick={() => navigate('/cliente/pedido')}
          className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white p-4 rounded-xl shadow-lg flex items-center justify-between transition-all hover:scale-[1.01] group"
          data-testid="cliente-novo-pedido-btn"
        >
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2.5 rounded-lg">
              <ShoppingCart className="w-5 h-5" />
            </div>
            <div className="text-left">
              <h3 className="font-bold">Monte seu Pedido</h3>
              <p className="text-xs opacity-90">
                Escolha produtos do catálogo
                {minOrder > 0 && <span className="ml-1 opacity-80">· mínimo {fmtBRL(minOrder)}</span>}
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </button>

        {/* === BANNER ROTATIVO === */}
        {destaques?.banners?.length > 0 && (
          <div className="relative rounded-2xl overflow-hidden shadow-lg group" data-testid="cliente-banner-section">
            <a
              href={destaques.banners[bannerIdx]?.link || '#'}
              target={destaques.banners[bannerIdx]?.link ? '_blank' : undefined}
              rel="noopener noreferrer"
              onClick={(e) => { if (!destaques.banners[bannerIdx]?.link) e.preventDefault(); }}
              className="block"
            >
              <img
                src={destaques.banners[bannerIdx]?.image_url || destaques.banners[bannerIdx]?.imagem_url}
                alt={destaques.banners[bannerIdx]?.titulo || destaques.banners[bannerIdx]?.title || 'Banner'}
                className="w-full h-40 sm:h-56 object-cover transition-opacity duration-500"
              />
              {(destaques.banners[bannerIdx]?.titulo || destaques.banners[bannerIdx]?.title) && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-4">
                  <h3 className="text-white text-base sm:text-xl font-bold drop-shadow-lg">
                    {destaques.banners[bannerIdx]?.titulo || destaques.banners[bannerIdx]?.title}
                  </h3>
                </div>
              )}
            </a>
            {destaques.banners.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                {destaques.banners.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setBannerIdx(i)}
                    className={`w-2 h-2 rounded-full transition-all ${i === bannerIdx ? 'bg-white w-6' : 'bg-white/50'}`}
                    aria-label={`Banner ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* === PROMOÇÕES === */}
        {destaques?.promo_products?.length > 0 && (
          <section data-testid="cliente-promo-section" className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-base flex items-center gap-2 text-orange-700 dark:text-orange-300">
                <Flame className="w-5 h-5 text-orange-500" />
                Ofertas Especiais
              </h2>
              <button
                onClick={() => navigate('/cliente/pedido')}
                className="text-xs text-orange-600 hover:underline font-medium"
              >
                Ver todos →
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {destaques.promo_products.slice(0, 8).map(p => (
                <ProdutoDestaqueCard key={p.id} produto={p} promo onClick={() => navigate('/cliente/pedido')} />
              ))}
            </div>
          </section>
        )}

        {/* === ADICIONADOS RECENTEMENTE === */}
        {destaques?.recent_products?.length > 0 && (
          <section data-testid="cliente-recents-section" className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-base flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                <Sparkles className="w-5 h-5 text-emerald-500" />
                Novidades no Catálogo
              </h2>
              <button
                onClick={() => navigate('/cliente/pedido')}
                className="text-xs text-emerald-600 hover:underline font-medium"
              >
                Ver catálogo →
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {destaques.recent_products.slice(0, 8).map(p => (
                <ProdutoDestaqueCard key={p.id} produto={p} onClick={() => navigate('/cliente/pedido')} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const COLORS = {
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
};

function StatCard({ icon: Icon, label, value, subtitle, color }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${COLORS[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{label}</p>
      <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
      {subtitle && <p className="text-[10px] text-gray-500 truncate">{subtitle}</p>}
    </div>
  );
}

function ProdutoDestaqueCard({ produto, promo, onClick }) {
  const foto = produto.fotos?.[0];
  const precoOriginal = produto.valor_unitario || 0;
  const precoFinal = promo && produto.preco_promocional > 0 ? produto.preco_promocional : precoOriginal;
  const desconto = produto.desconto_promocional || 0;
  return (
    <button
      onClick={onClick}
      className="group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-emerald-300 dark:hover:border-emerald-700 transition-all text-left"
      data-testid={`produto-destaque-${produto.id}`}
    >
      <div className="aspect-square bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
        {foto ? (
          <img
            src={foto}
            alt={produto.produto}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl font-bold text-gray-300">
            {(produto.produto || 'P')[0]}
          </div>
        )}
        {promo && desconto > 0 && (
          <span className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md flex items-center gap-1">
            <Flame className="w-3 h-3" /> -{desconto}%
          </span>
        )}
        {!promo && (
          <span className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Novo
          </span>
        )}
      </div>
      <div className="p-2.5 space-y-1">
        <p className="text-xs font-semibold leading-tight line-clamp-2 min-h-[2em] text-gray-900 dark:text-white">
          {produto.produto}
        </p>
        {produto.categoria && (
          <p className="text-[10px] text-gray-500 truncate">{produto.categoria}</p>
        )}
        <div className="flex items-baseline gap-1.5 pt-1">
          {promo && precoOriginal > precoFinal && precoOriginal > 0 && (
            <span className="text-[10px] text-gray-400 line-through">{fmtBRL(precoOriginal)}</span>
          )}
          {precoFinal > 0 && (
            <span className={`text-sm font-bold ${promo ? 'text-orange-600' : 'text-emerald-600'}`}>
              {fmtBRL(precoFinal)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
